import { ChangeEvent, useEffect, useState } from 'react';
import type { SwimInputData, BikeInputData, RunInputData } from '@/api/physioValueService';
import {
  calculateCSS,
  calculateCP,
  calculateCS,
  msToKmh,
  msToMinPerKm,
  formatPaceSeconds,
  formatDecimalMinutes,
} from '@opentri/shared/zones';
import TimeInput from '@/components/ui/TimeInput';

const numberCell =
  'w-24 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100';

const label = 'text-xs text-gray-400';
const hint = 'mb-2 text-sm text-gray-500 dark:text-gray-400';

const ResultBox = ({ children }: { children: React.ReactNode }) => (
  <div className="w-full rounded-lg bg-brand-50 p-3 text-gray-900 dark:bg-brand-500/10 dark:text-gray-100">
    {children}
  </div>
);

interface SwimTestInputProps {
  value: SwimInputData;
  onChange: (data: SwimInputData) => void;
}

export const SwimTestInput = ({ value, onChange }: SwimTestInputProps) => {
  const css = value.time1 > 0 && value.time2 > 0 && value.time2 > value.time1 ? calculateCSS(value) : 0;
  const swimPaceLabel = css > 0 ? formatPaceSeconds(100 / css) : '0:00';

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="w-full">
        <p className={hint}>Test court – Recommandation : 200 ou 400m</p>
        <div className="flex gap-4">
          <div className="flex flex-col gap-1">
            <span className={label}>Distance (m)</span>
            <input
              type="number"
              value={value.distance1 || ''}
              onChange={(e) => onChange({ ...value, distance1: parseInt(e.target.value) || 0 })}
              placeholder="200"
              className={numberCell}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className={label}>Temps</span>
            <TimeInput value={value.time1} onChange={(time1) => onChange({ ...value, time1 })} />
          </div>
        </div>
      </div>

      <div className="w-full">
        <p className={hint}>Test long – Recommandation : 400 ou 800m</p>
        <div className="flex gap-4">
          <div className="flex flex-col gap-1">
            <span className={label}>Distance (m)</span>
            <input
              type="number"
              value={value.distance2 || ''}
              onChange={(e) => onChange({ ...value, distance2: parseInt(e.target.value) || 0 })}
              placeholder="400"
              className={numberCell}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className={label}>Temps</span>
            <TimeInput value={value.time2} onChange={(time2) => onChange({ ...value, time2 })} />
          </div>
        </div>
      </div>

      {css > 0 && (
        <ResultBox>
          <p className="text-sm font-medium">Vitesse critique</p>
          <p className="text-xl font-bold">{swimPaceLabel} / 100m</p>
        </ResultBox>
      )}
    </div>
  );
};

interface BikeTestInputProps {
  value: BikeInputData;
  onChange: (data: BikeInputData) => void;
}

export const BikeTestInput = ({ value, onChange }: BikeTestInputProps) => {
  const cp = value.time1 > 0 && value.time2 > 0 && value.time1 > value.time2 ? calculateCP(value) : 0;

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="w-full">
        <p className={hint}>Test court – Recommandation : 3 ou 5min</p>
        <div className="flex gap-4">
          <div className="flex flex-col gap-1">
            <span className={label}>Durée</span>
            <TimeInput value={value.time2} onChange={(time2) => onChange({ ...value, time2 })} />
          </div>
          <div className="flex flex-col gap-1">
            <span className={label}>Puissance (W)</span>
            <input
              type="number"
              value={value.power2 || ''}
              onChange={(e) => onChange({ ...value, power2: parseInt(e.target.value) || 0 })}
              placeholder="300"
              className={numberCell}
            />
          </div>
        </div>
      </div>

      <div className="w-full">
        <p className={hint}>Test long – Recommandation : 12 ou 20min</p>
        <div className="flex gap-4">
          <div className="flex flex-col gap-1">
            <span className={label}>Durée</span>
            <TimeInput value={value.time1} onChange={(time1) => onChange({ ...value, time1 })} />
          </div>
          <div className="flex flex-col gap-1">
            <span className={label}>Puissance (W)</span>
            <input
              type="number"
              value={value.power1 || ''}
              onChange={(e) => onChange({ ...value, power1: parseInt(e.target.value) || 0 })}
              placeholder="250"
              className={numberCell}
            />
          </div>
        </div>
      </div>

      {cp > 0 && (
        <ResultBox>
          <p className="text-sm font-medium">Puissance critique</p>
          <p className="text-xl font-bold">{Math.round(cp)} W</p>
        </ResultBox>
      )}
    </div>
  );
};

interface RunTestInputProps {
  value: RunInputData;
  onChange: (data: RunInputData) => void;
}

export const RunTestInput = ({ value, onChange }: RunTestInputProps) => {
  const cs = value.time1 > 0 && value.time2 > 0 && value.time2 > value.time1 ? calculateCS(value) : 0;
  const speedKmh = cs > 0 ? msToKmh(cs) : 0;
  const runPaceLabel = cs > 0 ? formatDecimalMinutes(msToMinPerKm(cs)) : '0:00';

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="w-full">
        <p className={hint}>Test court – Recommandation : 3 ou 5min</p>
        <div className="flex gap-4">
          <div className="flex flex-col gap-1">
            <span className={label}>Durée</span>
            <TimeInput value={value.time1} onChange={(time1) => onChange({ ...value, time1 })} />
          </div>
          <div className="flex flex-col gap-1">
            <span className={label}>Distance (m)</span>
            <input
              type="number"
              value={value.distance1 || ''}
              onChange={(e) => onChange({ ...value, distance1: parseInt(e.target.value) || 0 })}
              placeholder="1000"
              className={numberCell}
            />
          </div>
        </div>
      </div>

      <div className="w-full">
        <p className={hint}>Test long – Recommandation : 12 ou 20min</p>
        <div className="flex gap-4">
          <div className="flex flex-col gap-1">
            <span className={label}>Durée</span>
            <TimeInput value={value.time2} onChange={(time2) => onChange({ ...value, time2 })} />
          </div>
          <div className="flex flex-col gap-1">
            <span className={label}>Distance (m)</span>
            <input
              type="number"
              value={value.distance2 || ''}
              onChange={(e) => onChange({ ...value, distance2: parseInt(e.target.value) || 0 })}
              placeholder="3000"
              className={numberCell}
            />
          </div>
        </div>
      </div>

      {cs > 0 && (
        <ResultBox>
          <p className="text-sm font-medium">Vitesse critique</p>
          <p className="text-xl font-bold">{speedKmh.toFixed(1)} km/h</p>
          <p className="text-sm">{runPaceLabel} / km</p>
        </ResultBox>
      )}
    </div>
  );
};

interface SwimDirectInputProps {
  value: number;
  onChange: (cssSwim: number) => void;
}

export const SwimDirectInput = ({ value, onChange }: SwimDirectInputProps) => {
  const total800mSeconds = value > 0 ? value * 8 : 0;
  const handleTimeChange = (seconds: number) => onChange(seconds > 0 ? seconds / 8 : 0);
  const swimDirectPaceLabel = value > 0 ? formatPaceSeconds(value) : '0:00';

  return (
    <div className="flex w-full flex-col items-start gap-2">
      <p className="text-sm text-gray-500 dark:text-gray-400">Temps sur 800m (mm:ss)</p>
      <TimeInput value={total800mSeconds} onChange={handleTimeChange} />
      {value > 0 && (
        <ResultBox>
          <p className="text-sm font-medium">Vitesse critique</p>
          <p className="text-xl font-bold">{swimDirectPaceLabel} / 100m</p>
        </ResultBox>
      )}
    </div>
  );
};

interface BikeDirectInputProps {
  value: number;
  onChange: (cp: number) => void;
}

export const BikeDirectInput = ({ value, onChange }: BikeDirectInputProps) => (
  <div className="flex w-full flex-col items-start gap-2">
    <p className="text-sm text-gray-500 dark:text-gray-400">Puissance max sur 45min (W)</p>
    <input
      type="number"
      value={value || ''}
      onChange={(e) => onChange(parseInt(e.target.value) || 0)}
      placeholder="250"
      className={numberCell}
    />
    {value > 0 && (
      <ResultBox>
        <p className="text-sm font-medium">Puissance critique</p>
        <p className="text-xl font-bold">{value} W</p>
        <p className="text-sm">PMA estimée: {Math.round(value * 1.2)} W</p>
      </ResultBox>
    )}
  </div>
);

const FLOAT_INPUT_REGEX = /^(\d+([.,]\d*)?)?$/;

interface RunDirectInputProps {
  value: number;
  onChange: (cs: number) => void;
}

export const RunDirectInput = ({ value, onChange }: RunDirectInputProps) => {
  const toDisplay = (ms: number) => (ms > 0 ? (ms * 3.6).toFixed(1) : '');
  const [rawInput, setRawInput] = useState<string>(() => toDisplay(value));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) setRawInput(toDisplay(value));
  }, [value, isFocused]);

  const speedKmh = value > 0 ? value * 3.6 : 0;
  const runDirectPaceLabel = value > 0 ? formatDecimalMinutes(msToMinPerKm(value)) : '0:00';

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (FLOAT_INPUT_REGEX.test(raw)) {
      setRawInput(raw);
      const parsed = parseFloat(raw.replace(',', '.'));
      onChange(isNaN(parsed) || parsed <= 0 ? 0 : parsed / 3.6);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const parsed = parseFloat(rawInput.replace(',', '.'));
    onChange(isNaN(parsed) || parsed <= 0 ? 0 : parsed / 3.6);
  };

  return (
    <div className="flex w-full flex-col items-start gap-2">
      <p className="text-sm text-gray-500 dark:text-gray-400">Vitesse max sur 45min (km/h)</p>
      <input
        type="text"
        inputMode="decimal"
        value={rawInput}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        placeholder="14.5"
        className={numberCell}
      />
      {value > 0 && (
        <ResultBox>
          <p className="text-sm font-medium">Vitesse critique</p>
          <p className="text-xl font-bold">{speedKmh.toFixed(1)} km/h</p>
          <p className="text-sm">{runDirectPaceLabel} / km</p>
          <p className="text-sm">VMA estimée: {(speedKmh * 1.2).toFixed(1)} km/h</p>
        </ResultBox>
      )}
    </div>
  );
};
